import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface ReputationBadgeProps {
  address: string;
  size?: 'sm' | 'md';
}

interface TierInfo {
  label: string;
  dotColor: string;
  badgeBg: string;
  badgeBorder: string;
  badgeText: string;
  tier: string;
}

function getTierInfo(totalTrades: number, completionRate: number): TierInfo {
  if (totalTrades >= 100 && completionRate >= 95)
    return {
      label: 'Cherry Elite',
      dotColor: 'bg-cherry-500',
      badgeBg: 'bg-cherry-50 dark:bg-cherry-900/20',
      badgeBorder: 'border-cherry-300 dark:border-cherry-700',
      badgeText: 'text-cherry-700 dark:text-cherry-300',
      tier: '100+ trades, 95%+ completion',
    };
  if (totalTrades >= 25 && completionRate >= 95)
    return {
      label: 'Top Trader',
      dotColor: 'bg-amber-500',
      badgeBg: 'bg-amber-50 dark:bg-amber-900/20',
      badgeBorder: 'border-amber-300 dark:border-amber-700',
      badgeText: 'text-amber-700 dark:text-amber-300',
      tier: '25+ trades, 95%+ completion',
    };
  if (totalTrades >= 5 && completionRate >= 90)
    return {
      label: 'Trusted',
      dotColor: 'bg-emerald-500',
      badgeBg: 'bg-emerald-50 dark:bg-emerald-900/20',
      badgeBorder: 'border-emerald-300 dark:border-emerald-700',
      badgeText: 'text-emerald-700 dark:text-emerald-300',
      tier: '5+ trades, 90%+ completion',
    };
  if (totalTrades >= 3)
    return {
      label: 'Active',
      dotColor: 'bg-blue-500',
      badgeBg: 'bg-surface-alt',
      badgeBorder: 'border-border',
      badgeText: 'text-text-primary',
      tier: '3+ trades',
    };
  if (totalTrades >= 1)
    return {
      label: 'Starter',
      dotColor: 'bg-gray-400',
      badgeBg: 'bg-surface-alt',
      badgeBorder: 'border-border',
      badgeText: 'text-text-secondary',
      tier: '1-2 trades',
    };
  return {
    label: 'New',
    dotColor: 'bg-gray-300 dark:bg-gray-600',
    badgeBg: 'bg-surface-alt',
    badgeBorder: 'border-border',
    badgeText: 'text-text-muted',
    tier: 'No trades yet',
  };
}

export function ReputationBadge({ address, size = 'sm' }: ReputationBadgeProps) {
  const [showTip, setShowTip] = useState(false);
  const tipRef = useRef<HTMLDivElement>(null);
  const { data } = useQuery({
    queryKey: ['reputation', address],
    queryFn: () => api.getReputation(address),
    staleTime: 5 * 60 * 1000,
    enabled: !!address,
  });

  useEffect(() => {
    if (!showTip) return;
    function close(e: MouseEvent) {
      if (tipRef.current && !tipRef.current.contains(e.target as Node)) setShowTip(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showTip]);

  if (!data) return null;

  const info = getTierInfo(data.totalTrades, data.completionRate);
  const isSmall = size === 'sm';

  return (
    <span className="relative inline-flex" ref={tipRef}>
      <button
        type="button"
        onClick={() => setShowTip((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-full font-medium cursor-help border ${info.badgeBg} ${info.badgeBorder} ${info.badgeText} ${
          isSmall ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5 text-xs'
        }`}
        aria-label={`Reputation: ${info.label} — ${data.totalTrades} trades, ${data.completionRate}% completion`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${info.dotColor}`} />
        <span>{info.label}</span>
        {data.totalTrades > 0 && (
          <>
            <span className="opacity-50">·</span>
            <span>{data.totalTrades}</span>
          </>
        )}
      </button>

      {showTip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-surface border border-border rounded-lg shadow-(--shadow-modal) p-3 z-50 text-xs text-text-secondary">
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className={`w-2 h-2 rounded-full shrink-0 ${info.dotColor}`} />
            <p className="font-semibold text-text-primary">{info.label}</p>
            <span className="text-text-muted">({info.tier})</span>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-text-muted">Trades completed</span>
              <span className="font-medium text-text-primary">{data.totalTrades}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Success rate</span>
              <span className="font-medium text-text-primary">{data.completionRate}%</span>
            </div>
            {data.totalVolumeTon && data.totalVolumeTon !== '0' && (
              <div className="flex justify-between">
                <span className="text-text-muted">Volume</span>
                <span className="font-medium text-text-primary">{data.totalVolumeTon} TON</span>
              </div>
            )}
          </div>
          <p className="mt-2 pt-1.5 border-t border-border text-text-muted">
            Calculated from on-chain settlement data.
          </p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-surface border-r border-b border-border rotate-45 -mt-1" />
        </div>
      )}
    </span>
  );
}
